/**
 * @file PortalTemplatesPreview.js
 * @description This page displays the preview of a selected portal template.
 */

import '../page-list/PageList.css';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import { ROUTES } from '../../constants/routes';
import { readPortalPage } from '../../services/portalPageService';
import { SecurityAttributes } from '../../utils/data/enums';
import { useAuthContext } from '../../store/AuthContext';
import FreeformGrid from '../fluid-grid/FreeformGrid';
import { getText } from '../../i18n';

const PortalTemplatePreview = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const { hasSecurityAttribute } = useAuthContext();

    const { state } = useLocation();
    const template = state?.template;
    const fromMultiTenant = state?.fromMultiTenant || false;

    const navigateBack = () => {
        const destination = fromMultiTenant
            ? ROUTES.TEMPLATES
            : ROUTES.PORTAL_TEMPLATES;
        navigate(destination);
    };

    const handleClose = () => {
        return navigateBack();
    };

    const handleCreatePortal = () => {
        if (fromMultiTenant) {
            navigate(ROUTES.TEMPLATES_NEW, {
                state: {
                    loadFromMultiTenantTemplates: true,
                    loadFromTemplate: true,
                    id: template.id,
                    template,
                },
            });
            return;
        }
        navigate(ROUTES.PORTAL_PAGES_NEW, {
            state: {
                id: template.id,
                loadFromTemplate: true,
                template: template,
            },
        });
    };

    const handleEditPortal = () => {
        navigate(ROUTES.PORTAL_TEMPLATES_NEW, {
            state: {
                loadFromClientTemplates: true,
                id: template.id,
            },
        });
    };

    const [fetchedContainers, setFetchedContainers] = useState([]);
    const [fetchedMobileConfiguration, setFetchedMobileConfiguration] =
        useState([]);

    async function loadPortalPage(template) {
        if (!template) return;
        let fetchedPortalPage = {};
        try {
            setIsLoading(true);
            fetchedPortalPage = await readPortalPage(
                template.id,
                true,
                template.tenantId === null ? 0 : template.tenantId,
            );
        } catch (e) {
            console.error(e);
            Toast.error(getText('msg_eeceb57efc21'));
        } finally {
            setFetchedContainers(fetchedPortalPage.containers || []);
            setFetchedMobileConfiguration(
                fetchedPortalPage?.viewModels?.[0]?.mobileConfiguration || [],
            );
            setIsLoading(false);
        }
    }

    useEffect(() => {
        loadPortalPage(template);
    }, [template]);

    return isLoading ? (
        <Spinner width={50} className="pageListloadingSpinner" />
    ) : (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div
                            style={{ display: 'flex', flexDirection: 'column' }}
                            className="mb-4"
                        >
                            <p className="page-title float-start card-title ms-4">
                                {getText('msg_f1fbb2b43dca')}
                            </p>
                            <p
                                className="ing-portals-subtitle mt-1"
                                style={{ marginTop: '10px' }}
                            >
                                {getText('msg_f00cd49d3a37')}
                            </p>
                        </div>
                        {!hasSecurityAttribute([
                            SecurityAttributes.TemplatesReadOnly,
                            SecurityAttributes.MultiTenantPortalTemplatesReadOnly,
                        ]) && (
                            <div className="float-end d-flex align-items-center">
                                <span
                                    type="button"
                                    onClick={handleClose}
                                    className="ing-text-buttons mx-3"
                                >
                                    <i className="bi bi-x me-2" />
                                    {getText('msg_bbfa773e5a63')}
                                </span>
                                {template.tenantId !== null && (
                                    <span
                                        type="button"
                                        onClick={() => handleEditPortal()}
                                        className="ing-text-buttons mx-3"
                                    >
                                        <i className="bi bi-pencil me-2" />
                                        {getText('msg_5301648dcf6b')}
                                    </span>
                                )}
                                <span
                                    onClick={() => handleCreatePortal()}
                                    type="button"
                                    className="ms-3 me-4 button-base ing-button-primary-left align-middle"
                                >
                                    <i className="bi bi-plus-lg" />
                                    {getText('msg_d47bf0875676')}
                                </span>
                            </div>
                        )}
                    </div>

                    <div
                        style={{
                            background: '#e9eaed',
                            width: 'fit-content',
                            padding: '40px',
                            margin: '0 auto',
                        }}
                    >
                        <FreeformGrid
                            fetchedContainers={fetchedContainers}
                            fetchedMobileConfiguration={
                                fetchedMobileConfiguration
                            }
                            navigationStack={[]}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PortalTemplatePreview;
